import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { adminApi, apiError } from "@/components/admin/api";

const WEEKDAYS = [
  { key: "0", label: "Segunda-feira" },
  { key: "1", label: "Terça-feira" },
  { key: "2", label: "Quarta-feira" },
  { key: "3", label: "Quinta-feira" },
  { key: "4", label: "Sexta-feira" },
];

const AvailabilityView = () => {
  const [days, setDays] = useState(null);
  const [maxDays, setMaxDays] = useState(60);
  const [newTime, setNewTime] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .get("/admin/journey/schedule")
      .then((res) => {
        setDays(res.data.days || {});
        setMaxDays(res.data.max_days_ahead ?? 60);
      })
      .catch((e) => toast.error(apiError(e, "Não foi possível carregar a disponibilidade.")));
  }, []);

  const addTime = (key) => {
    const t = (newTime[key] || "").trim();
    if (!/^\d{2}:\d{2}$/.test(t)) {
      toast.error("Escolha um horário válido.");
      return;
    }
    setDays((d) => ({ ...d, [key]: [...(d[key] || []), t].sort() }));
    setNewTime((n) => ({ ...n, [key]: "" }));
  };

  const removeTime = (key, t) =>
    setDays((d) => ({ ...d, [key]: (d[key] || []).filter((x) => x !== t) }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/journey/schedule", { days, max_days_ahead: Number(maxDays) || 60 });
      toast.success("Disponibilidade atualizada. Os pacientes já veem os novos horários.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!days) {
    return <div className="h-64 animate-pulse rounded-3xl border border-raiz-900/10 bg-cream" data-testid="disponibilidade-loading" />;
  }

  return (
    <div data-testid="disponibilidade-view">
      <p className="max-w-2xl text-sm leading-relaxed text-ink/60">
        Defina os horários que os pacientes podem escolher no site. Agendamentos já feitos não são alterados.
      </p>
      <div className="mt-8 space-y-4">
        {WEEKDAYS.map((w) => {
          const times = days[w.key] || [];
          return (
            <div key={w.key} data-testid={`disp-dia-${w.key}`} className="rounded-2xl border border-raiz-900/10 bg-paper p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-serif text-lg text-ink">{w.label}</p>
                {times.length === 0 && (
                  <span className="rounded-full border border-red-500/40 px-3 py-1 text-xs font-medium text-red-700" data-testid={`disp-indisponivel-${w.key}`}>
                    Indisponível
                  </span>
                )}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {times.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-raiz-800/20 bg-cream px-3.5 py-1.5 text-sm text-raiz-900" data-testid={`disp-time-${w.key}-${t.replace(":", "")}`}>
                    {t}
                    <button
                      type="button"
                      aria-label={`Remover ${t}`}
                      data-testid={`disp-remover-${w.key}-${t.replace(":", "")}`}
                      onClick={() => removeTime(w.key, t)}
                      className="text-ink/40 transition-colors hover:text-red-600"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
                <span className="inline-flex items-center gap-2">
                  <input
                    type="time"
                    aria-label={`Novo horário de ${w.label}`}
                    data-testid={`disp-novo-${w.key}`}
                    value={newTime[w.key] || ""}
                    onChange={(e) => setNewTime((n) => ({ ...n, [w.key]: e.target.value }))}
                    className="rounded-full border border-raiz-800/20 bg-paper px-3 py-1.5 text-sm text-ink focus:border-raiz-700 focus:outline-none"
                  />
                  <button
                    type="button"
                    data-testid={`disp-adicionar-${w.key}`}
                    onClick={() => addTime(w.key)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-raiz-800 px-3.5 py-1.5 text-xs font-medium text-paper transition-colors hover:bg-raiz-700"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Adicionar
                  </button>
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-raiz-900/10 bg-paper p-5">
        <label htmlFor="max-days" className="text-sm text-ink/70">
          Agenda aberta pelos próximos
        </label>
        <input
          id="max-days"
          type="number"
          min={7}
          max={180}
          data-testid="disp-max-days"
          value={maxDays}
          onChange={(e) => setMaxDays(e.target.value)}
          className="w-24 rounded-full border border-raiz-800/20 bg-paper px-4 py-2 text-sm text-ink focus:border-raiz-700 focus:outline-none"
        />
        <span className="text-sm text-ink/70">dias</span>
      </div>

      <button
        type="button"
        data-testid="disp-salvar-button"
        onClick={save}
        disabled={saving}
        className="mt-8 rounded-full bg-raiz-800 px-8 py-3.5 text-sm font-medium tracking-wide text-paper transition-colors hover:bg-raiz-700 disabled:opacity-60"
      >
        {saving ? "Salvando…" : "Salvar alterações"}
      </button>
    </div>
  );
};

export default AvailabilityView;
